// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { createReducer } from '@reduxjs/toolkit';
import { DATASTORE_ACTIONS } from '../../commons/DatastoreConstants';

const addIfNotInArray = (array, newElement) => {
  if (array.find((el) => el === newElement) === undefined) {
    array.push(newElement);
  }
};

const removePathFromArray = (array, pathToRemove) => {
  const indexOfPathToRemove = array.findIndex((path) => path === pathToRemove);
  if (indexOfPathToRemove === -1) {
    console.warn(`Workspace file path "${pathToRemove}" not found, can't delete it`);
    return;
  }

  array.splice(indexOfPathToRemove, 1);
};

const DEFAULT_VALIDATION_STATE = { status: null, details: [], tables: {} };
export const datastoreInitialState = {
  selectedDatasetId: null,
  runners: [],
  workspaceFilePaths: [],
  loading: false,
  networkRequestPending: false,
  validationState: DEFAULT_VALIDATION_STATE,
};

export const datastoreReducer = createReducer(datastoreInitialState, (builder) => {
  builder
    .addCase(DATASTORE_ACTIONS.SELECT_DATASTORE, (state, action) => {
      state.selectedDatasetId = action.datasetId;
    })
    .addCase(DATASTORE_ACTIONS.SELECT_DEFAULT_DATASTORE, (state, action) => {
      state.selectedDatasetId = action.selectableDatasets?.[0]?.id;
    })
    .addCase(DATASTORE_ACTIONS.ADD_RUNNER, (state, action) => {
      state.runners.push(action.runner);
    })
    .addCase(DATASTORE_ACTIONS.UPDATE_RUNNER, (state, action) => {
      const indexOfRunnerToUpdate = state.runners.findIndex((runner) => runner.id === action.runnerId);
      if (indexOfRunnerToUpdate === -1) {
        console.warn(`Runner with id "${action.runnerId}" not found, can't update it`);
        return;
      }

      state.runners[indexOfRunnerToUpdate] = { ...state.runners[indexOfRunnerToUpdate], ...action.runnerData };
    })
    .addCase(DATASTORE_ACTIONS.DELETE_RUNNER, (state, action) => {
      const indexOfPathToRemove = state.runners.findIndex((runner) => runner.id === action.runnerId);
      if (indexOfPathToRemove === -1) {
        console.warn(`Runner with id "${action.runnerId}" not found, can't delete it`);
        return;
      }

      state.runners.splice(indexOfPathToRemove, 1);
    })
    .addCase(DATASTORE_ACTIONS.SET_RUNNERS, (state, action) => {
      state.runners = action.runners;
    })
    .addCase(DATASTORE_ACTIONS.SET_WORKSPACE_FILE_PATHS, (state, action) => {
      state.workspaceFilePaths = [...action.workspaceFilePaths];
    })
    .addCase(DATASTORE_ACTIONS.ADD_WORKSPACE_FILE_PATH, (state, action) => {
      addIfNotInArray(state.workspaceFilePaths, action.workspaceFilePath);
    })
    .addCase(DATASTORE_ACTIONS.ADD_WORKSPACE_FILE_PATHS, (state, action) => {
      action.workspaceFilePaths.forEach((path) => addIfNotInArray(state.workspaceFilePaths, path));
    })
    .addCase(DATASTORE_ACTIONS.REMOVE_WORKSPACE_FILE_PATH, (state, action) => {
      removePathFromArray(state.workspaceFilePaths, action.workspaceFilePath);
    })
    .addCase(DATASTORE_ACTIONS.REMOVE_WORKSPACE_FILE_PATHS, (state, action) => {
      action.workspaceFilePaths.forEach((path) => removePathFromArray(state.workspaceFilePaths, path));
    })
    .addCase(DATASTORE_ACTIONS.SET_LOADING, (state, action) => {
      state.loading = action.loading;
    })
    .addCase(DATASTORE_ACTIONS.SET_NETWORK_REQUEST_PENDING, (state, action) => {
      state.networkRequestPending = action.networkRequestPending;
    })
    .addCase(DATASTORE_ACTIONS.RESET_VALIDATION_STATE, (state) => {
      state.validationState = DEFAULT_VALIDATION_STATE;
    })
    .addCase(DATASTORE_ACTIONS.SET_VALIDATION_STATE, (state, action) => {
      state.validationState = action.validationState;
    })
    .addCase(DATASTORE_ACTIONS.UPDATE_VALIDATION_STATE, (state, action) => {
      state.validationState = { ...state.validationState, ...action.validationStatePatch };
    });
});
